import React, { Component } from 'react';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import 'react-toggle/style.css';
import InputField from '../../inputs/inputField.js';
import FieldModal from '../../modals/fieldModal.js';
import SelectField from '../../inputs/selectField.js';
import API from '../../../utils/API';

const dataOptions = [
	{ value: 'Attendee', label: 'Attendees' },
	{ value: 'Session', label: 'Sessions' },
	{ value: 'Group', label: 'Groups' },
	{ value: 'Email', label: 'Emails' },
	{ value: 'Submission', label: 'Submissions' } 
];

const chartOptions = [
	{ value: 'Doughnut', label: 'Doughnut Chart' },
	{ value: 'Line', label: 'Line Chart' },
	{ value: 'Bar', label: 'Bar Chart' }
];

class AddReportGraphModal extends Component {
	constructor(props) {
		super(props);
		this.state = {
			submitting: false,
			selectedDataOption: null,
			selectedChartOption: chartOptions[0],
			selectedFilterOption: null,
			selectedForm: null,
			name: '',
			view: '',
			viewID: '',
			viewSelects: this.formatViewOptions(props.customViews),
			defaultViewSelects: this.formatViewOptions(props.customViews),
			selectedView: { label: '', color: '#00B8D9' },
			viewExists: false,
			columnsOrder: [],
			requiredFields: {
				name: false,
				dataType: false,
				filter: false,
				formId: false
			}
		};
	}

	getColumnsOrder = (selectedDataOption) => {
		if (!selectedDataOption) return [];

		const mappings = {
			Attendee: this.props.attendee,
			Session: this.props.session,
			Group: this.props.group,
			Email: this.props.email
		};

		const selectedMapping = mappings[selectedDataOption.value];

		if (!selectedMapping) return [];

		return Object.entries(selectedMapping)
			.map(([key, label]) => ({
				value: key,
				label: label
			}))
			.sort((a, b) => a.label.localeCompare(b.label));
	};
	getFormFieldOptions = () => {
		const { selectedForm } = this.state;
		const { forms } = this.props;
		
		if (!selectedForm || !forms) return [];
		
		const form = forms.find(f => f._id === selectedForm.value);
		if (!form || !form.submissions || form.submissions.length === 0) return [];
		
		// Get a sample submission to extract field keys
		const sampleSubmission = form.submissions[0];
		const fieldOptions = [];
		
		// Add basic form data fields
		if (sampleSubmission.formData) {
			Object.keys(sampleSubmission.formData).forEach(key => {
				if (key !== 'extraFields' && key !== 'additionalAttendees') {
					fieldOptions.push({
						value: key,
						label: this.formatFieldLabel(key)
					});
				}
			});
			
			// Parse extraFields to get custom field keys
			if (sampleSubmission.formData.extraFields) {
				try {
					const extraFields = typeof sampleSubmission.formData.extraFields === 'string' 
						? JSON.parse(sampleSubmission.formData.extraFields)
						: sampleSubmission.formData.extraFields;
						
					Object.keys(extraFields).forEach(key => {
						fieldOptions.push({
							value: key,
							label: this.formatFieldLabel(key)
						});
					});
				} catch (e) {
					console.log('Error parsing extraFields:', e);
				}
			}
		}
		
		// Add submission metadata fields
		fieldOptions.push(
			{ value: 'submittedAt', label: 'Submitted At' },
			{ value: 'formId', label: 'Form ID' },
			{ value: '_id', label: 'Submission ID' }
		);
		
		return fieldOptions.sort((a, b) => a.label.localeCompare(b.label));
	};
	// Helper method to format field labels
	formatFieldLabel = (key) => {
		// Check if it's a MongoDB ObjectId (24 hex characters)
		if (/^[a-f0-9]{24}$/.test(key)) {
			return `Field ${key}`;
		}
		
		// Convert camelCase to Title Case
		return key
			.replace(/([A-Z])/g, ' $1')
			.replace(/^./, str => str.toUpperCase())
			.replace(/_/g, ' ');
	};
	// Handle data type selection change
	handleDataOptionChange = (selectedDataOption) => {
		const columnsOrder = this.getColumnsOrder(selectedDataOption);
		this.setState({
			selectedDataOption,
			columnsOrder,
			selectedFilterOption: null, // Reset filter when data type changes
			selectedForm: null
		});
	};
	handleFormChange = (selectedForm) => {
		this.setState({ 
			selectedForm,
			selectedFilterOption: null // Reset filter when form changes
		});
	};
	// Add this method
	formOptions = () => {
		const { forms } = this.props; // Make sure forms are passed as props
		if (!forms) return [];
		
		return forms.map(form => ({
			value: form._id,
			label: form.name
		}));
	};

	getViewColumns = (selectedView) => {
		if (!selectedView || !this.props.customViews) return [];

		const view = this.props.customViews[selectedView.value];
		if (!view) return [];

		// Get columns that are toggled on in the view and sort them
		const activeColumns = view.normalizedColumns
			.filter(col => view.toggledColumns[col.key])
			.map(col => ({
				value: col.key,
				label: col.label || col.key
			}))
		return activeColumns;
	};


	getGraphDataForType = (dataType, selectedColumn, viewId) => {
		const { allEntries, customViews} = this.props;
		let entriesForType = allEntries[dataType] || [];
		 // If dataType is Submission, filter by formId for the current view
		if (dataType === 'Submission') {
			const view = customViews.find(v => v._id === viewId);
			if (view && view.formId) {
				entriesForType = entriesForType.filter(entry => entry.formId === view.formId);
			}
		}
		const counts = {};
		const timestampFields = ['createdAt', 'updatedAt', 'rsvpAt', 'registeredAt','submittedAt'];
		const isTimestampField = timestampFields.includes(selectedColumn);
		let displayLabel = selectedColumn;
		if (selectedColumn && selectedColumn.includes('*')) {
			const fieldMapping = customViews.find(view => view._id === viewId);
            displayLabel = fieldMapping.customFieldColumns[selectedColumn];  
		} else {
			let fieldMapping;
			switch(dataType) {
				case 'Attendee': fieldMapping = this.props.attendee; break;
				case 'Session': fieldMapping = this.props.session; break;
				case 'Group': fieldMapping = this.props.group; break;
				case 'Email': fieldMapping = this.props.email; break;
				case 'Submission': fieldMapping = this.props.submission; break;
				default: fieldMapping = {};
			}
			displayLabel = fieldMapping[selectedColumn] || selectedColumn;
		}
		
		entriesForType.forEach((entry) => {
			let value = entry[selectedColumn];
			
			if (typeof value === 'boolean') {
				value = value ? 'Yes' : 'No';
			}
			
			// Handle timestamp values
			let keyForCount = value;
			if (isTimestampField && value) {
				try {
					const date = new Date(value);
					// Format to date and hour only (no seconds) for display
					keyForCount = date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'});
				} catch (e) {
					console.log('Error parsing date:', value);
				}
			}
			
			// Use keyForCount for timestamps, otherwise use the standard value processing
			const valueKey = value === null || value === undefined || value === '' ? 
				'empty_value' : 
				(isTimestampField && value) ? 
					String(keyForCount) : 
					String(value);
			
			counts[valueKey] = (counts[valueKey] || 0) + 1;
		});
		
		const uniqueValues = Object.keys(counts);
		
		const formattedLabels = uniqueValues.map(value => {
			const count = counts[value];
			const displayValue = value === 'empty_value' ? `Blank` : value;
			return `${displayValue} (${count})`;  
		});
		
		return {
			label: displayLabel,
			data: uniqueValues.map(value => counts[value]),
			labels: formattedLabels,
		};
	};

	formatCustomViewOptions = (customViews) => {
		if (!customViews) return [];
		return Object.entries(customViews).map(([index, view]) => ({
			value: index,
			label: view.name,
			type: view.type,
			columnsOrder: view.columnsOrder,
			toggledColumns: view.toggledColumns
		}));
	};


	formatViewOptions = (customViews) => {
		if (!customViews) return [];

		return Object.entries(customViews).map(([index, view]) => ({
			value: index,
			label: view.name,
			color: '#00B8D9',
			toggledColumns: view.toggledColumns,
			normalizedColumns: view.normalizedColumns,
			type: view.type,
			_id: view._id
		}));
	};

	handleViewNameChange = (inputValue) => {
		const existingViews = this.state.defaultViewSelects;
		const lowerCaseInput = inputValue.toLowerCase();
		const viewExists = existingViews.some(view =>
			view.label.toLowerCase() === lowerCaseInput
		);

		if (viewExists) {
			this.setState({ viewExists: true });
			return;
		}

		const newView = {
			label: 'New view: ' + inputValue,
			new: true
		};

		this.setState(prevState => ({
			viewExists: false,
			viewSelects: [...prevState.defaultViewSelects, newView]
		}));
	};


	handleViewChosen = (selectedOption) => {
		if (selectedOption.new) {
			this.setState({
				selectedView: {
					label: selectedOption.label.split('New view: ')[1]
				},
				isNewView: true,
				// Reset data option when creating new view
				selectedDataOption: null
			});
		} else {
			this.setState({
				selectedView: selectedOption,
				isNewView: false,
				columnsOrder: selectedOption.columnsOrder || [],
				toggledColumns: selectedOption.toggledColumns || {},
				selectedDataOption: dataOptions.find(opt => opt.value === selectedOption.type),
				viewID: selectedOption._id
			});
		}
	};

	saveNewView = () => {
		return new Promise((resolve, reject) => {
			const { orgId, referenceCollection, referenceId } = this.props;
			const { selectedDataOption, selectedView, selectedForm } = this.state;
			
			const view = {
				name: selectedView.label,
				type: selectedDataOption.value,
				referenceCollection: referenceCollection,
				referenceId: referenceId,
				toggledColumns: [],
			};
	
			// Add formId for Submission views
			if (selectedDataOption.value === 'Submission' && selectedForm) {
				view.formId = selectedForm.value;
			}
	
			const requiredFields = {
				name: !view.name || view.name === '',
				type: !view.type || view.type === '',
				formId: selectedDataOption.value === 'Submission' && !selectedForm  // Only require for new Submission views
			};
	
			const valid = !Object.values(requiredFields).some(Boolean);
	
			if (valid) {
				view.filterLayers = [];
				view.columnsOrder = [];
	
				this.setState({ submitting: true });
				API()
					.post(`/Organizations/${orgId}/views`, { ...view, reportId: this.props.reportId })
					.then((res) => {
						this.setState({ 
							submitting: false, 
							requiredFields: { name: false, type: false, formId: false }, 
							viewID: res.data._id 
						}, () => {
							this.props.addView(res.data);
							resolve(res.data._id);
						});
					})
					.catch((err) => {
						this.setState({ submitting: false });
						console.log(err);
						reject(err);
					});
			} else {
				this.setState({ requiredFields });
				reject(new Error("Invalid view data"));
			}
		});
	};


	saveNewGraph = async () => {
		const { 
			selectedDataOption, 
			selectedChartOption, 
			selectedFilterOption, 
			selectedForm,
			name, 
			isNewView, 
			selectedView 
		} = this.state;
		const { orgId, reportId, eventId } = this.props;
		
		// Enhanced validation - only require form for NEW Submission views
		const newRequiredFields = {
			name: !name,
			dataType: !selectedDataOption,
			filter: !selectedFilterOption,
			formId: isNewView && selectedDataOption?.value === 'Submission' && !selectedForm
		};
	
		if (Object.values(newRequiredFields).some(Boolean)) {
			this.setState({ requiredFields: newRequiredFields });
			return;
		}
	
		// Reset required fields if validation passes
		this.setState({ 
			requiredFields: { name: false, dataType: false, filter: false, formId: false } 
		});
	
		// Rest of your existing logic...
		if (isNewView) {
			try {
				await this.saveNewView(); 
			} catch (error) {
				console.error("Error creating view:", error);
				this.setState({
					submitting: false,
					error: 'Failed to create view'
				});
				return; 
			}
		}
	
		const graphData = this.getGraphDataForType(selectedDataOption.value, selectedFilterOption.value, this.state.viewID);
	
		const generateBlueShades = (count) => {
			const hue = 210;        
			const saturation = 83;  
			const baseAlpha = 0.8;   
			
			const actualCount = Math.max(count, 2);
	
			const shades = [];
			for (let i = 0; i < actualCount; i++) {
				  const lightness = 45 + ((90 - 45) * i) / (actualCount - 1);
				shades.push(`hsla(${hue}, ${saturation}%, ${lightness}%, ${baseAlpha})`);
			}
			return shades;
		};
		const backgroundColor = generateBlueShades(graphData.labels.length);
	
		const newGraph = {
			name: name,
			type: selectedChartOption.value,
			dataType: selectedDataOption.value,
			filterBy: selectedFilterOption.value,
			data: {
				labels: graphData.labels,
				datasets: [{
					label: graphData.label,
					data: graphData.data,
					backgroundColor,
				}],
			},
			viewID: this.state.viewID
		};
	
		try {
			this.setState({ submitting: true });
			const res = await API().patch(`/Organizations/${orgId}/events/${eventId}/viewReports/${reportId}`, { graphs: newGraph });
	
			if (res.data) {
				const newGraphId = res.data.graphs[res.data.graphs.length - 1]._id;
				newGraph._id = newGraphId;
				this.props.addGraph(newGraph);
				this.props.toggle();
			} else {
				console.error('No data returned from API');
				// Handle error case
			}
	
		} catch (error) {
			console.error('Error adding graph:', error);
			// Show error to user
			this.setState({
				error: 'Failed to save graph'
			});
		} finally {
			this.setState({ submitting: false });
		}
	};
	render() {
		const { isOpen, toggle } = this.props;
		const {
			submitting,
			name,
			requiredFields,
			selectedDataOption,
			selectedChartOption,
			selectedFilterOption,
			columnsOrder,
			viewSelects,
			viewExists,
			selectedView,
			isNewView,
			selectedForm
		} = this.state;
		
		const getFilterOptions = () => {
			if (!selectedView.new && selectedView.value) {
				return this.getViewColumns(selectedView).sort((a, b) => a.label.localeCompare(b.label));
			}
			if (selectedDataOption?.value === 'Submission' && selectedForm) {
				return this.getFormFieldOptions();
			}
			return columnsOrder.sort((a, b) => a.label.localeCompare(b.label));
		};

		return (
			<div>
				<FieldModal
					size="medium"
					isOpen={isOpen}
					modalTitle="Add Chart to Report"
					bodyHeaderText="Please enter the information below to add new chart."
					bodyDescription=""
					overflowOff={true}
					bodyContent={() => (
						<>
							<SelectField
								classes={"w-100"}
								label="View Name"
								value={selectedView}
								style={{ padding: 0 }}
								name="viewName"
								options={viewSelects}
								required={true}
								errorTrigger={requiredFields.viewName || viewExists}
								onChange={this.handleViewChosen}
								onInputChange={this.handleViewNameChange}
								errorMessage={viewExists ? "View already exists" : ""}
							/>

							{(selectedView.new || isNewView) && (
								<SelectField
									label="Data Set"
									value={selectedDataOption}
									options={dataOptions}
									onChange={this.handleDataOptionChange}
									isSearchable={false}
									required={true}
									errorTrigger={requiredFields.dataType}
								/>
							)}
							{(selectedView.new || isNewView) && selectedDataOption && selectedDataOption.value === 'Submission' && (
								<SelectField
									value={selectedForm}
									label={'Select Form'}
									required={true}
									errorTrigger={requiredFields.formId}
									options={this.formOptions()}
									onChange={this.handleFormChange}
								/>
							)}

							<SelectField
								label="Chart Type"
								value={selectedChartOption}
								options={chartOptions}
								onChange={(selected) => this.setState({ selectedChartOption: selected })}
								isSearchable={false}
							/>

							<SelectField
								label="Chart Filter"
								value={selectedFilterOption}
								options={getFilterOptions()}
								onChange={(selected) => this.setState({ selectedFilterOption: selected })}
								isDisabled={
									(!columnsOrder.length && (!selectedView.value || selectedView.new)) ||
									(selectedDataOption?.value === 'Submission' && !selectedForm)
								}
								required={true}
								errorTrigger={requiredFields.filter}
							/>
							<InputField
								value={name}
								label="Graph Name"
								required={true}
								errorTrigger={requiredFields.name}
								onChange={(e) => this.setState({ name: e.target.value })}
							/>
						</>
					)}
					toggle={toggle}
					actionButtonLabel={submitting ? 'Adding...' : 'Submit'}
					actionButton={this.saveNewGraph}
					actionButtonDisabled={submitting}
				/>
			</div>
		);
	}
}

export default AddReportGraphModal;